import Cookies from "js-cookie";
import { useCashierStore } from "@/store/cashier.store";
import { useSaleStore } from "@/store/sale.store";

/** Resuelve el id de caja: cookie → store → objeto cashier persistido */
export function resolveCashierId(): string | undefined {
  const fromCookie = Cookies.get("cashierId");
  if (fromCookie) return fromCookie;

  const { cashierId, cashier } = useCashierStore.getState();
  if (cashierId) return String(cashierId);
  if (cashier?.id) return String(cashier.id);

  return undefined;
}

/** Alinea la cookie cashierId con el store persistido (el interceptor Axios lee la cookie) */
export function syncCashierIdToCookieAndHeaders(): void {
  const id = resolveCashierId();
  if (!id) return;

  if (Cookies.get("cashierId") !== id) {
    Cookies.set("cashierId", id, { expires: 3 });
  }
}

export function hasPendingOfflineSalesInStore(): boolean {
  return useSaleStore.getState().getUnsyncedSales().length > 0;
}

export async function hasAnyPendingOfflineWork(): Promise<boolean> {
  if (hasPendingOfflineSalesInStore()) return true;

  const { getPendingRequests } = await import("@/utilities/offline-db");
  const pending = await getPendingRequests();
  return pending.length > 0;
}

/** Caja abierta, sesión con cashier-id y conexión del navegador */
export function canRunOfflineSync(): boolean {
  if (!navigator.onLine) return false;

  syncCashierIdToCookieAndHeaders();

  const cashierId = resolveCashierId();
  if (!cashierId) return false;

  const { cashier } = useCashierStore.getState();
  if (!cashier || cashier.status !== "opened") return false;

  return true;
}

/** Headers para POST offline (PouchDB / resilientPost) con la misma sesión que Axios */
export function buildApiHeadersFromSession(
  base: Record<string, string> = {}
): Record<string, string> {
  syncCashierIdToCookieAndHeaders();

  const result: Record<string, string> = { ...base };

  const token = Cookies.get("authToken");
  if (token && !result["Authorization"]) {
    result["Authorization"] = `Bearer ${token}`;
  }

  const storeId = Cookies.get("storeId");
  if (storeId && !result["store-id"]) {
    result["store-id"] = storeId;
  }

  const cashierId = resolveCashierId();
  if (cashierId && !result["cashier-id"]) {
    result["cashier-id"] = cashierId;
  }

  const companyTaxId = Cookies.get("companyTaxId");
  if (companyTaxId && !result["company-tax-id"]) {
    result["company-tax-id"] = companyTaxId;
  }

  if (!result["Content-Type"]) {
    result["Content-Type"] = "application/json";
  }

  return result;
}

export function waitForSaleStoreHydration(): Promise<void> {
  const persist = useSaleStore.persist;
  if (persist.hasHydrated()) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const unsub = persist.onFinishHydration(() => {
      unsub();
      resolve();
    });
  });
}

export function waitForCashierStoreHydration(): Promise<void> {
  const persist = useCashierStore.persist;
  if (persist.hasHydrated()) {
    syncCashierIdFromPersistedState();
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    const unsub = persist.onFinishHydration(() => {
      unsub();
      syncCashierIdFromPersistedState();
      resolve();
    });
  });
}

function syncCashierIdFromPersistedState(): void {
  const { cashierId, cashier, setCashierId } = useCashierStore.getState();
  const id = cashierId ?? cashier?.id;
  if (id) {
    setCashierId(id);
  }
}

export async function waitForSyncPrerequisites(): Promise<void> {
  await Promise.all([
    waitForSaleStoreHydration(),
    waitForCashierStoreHydration(),
  ]);
}
